<template>
  <div>
    <!-- Results Count Header -->
    <div v-if="items.length" class="text-m text-racq-med-grey py-4">
      <slot name="header">
        <span v-if="resultCountLabel" class="text-racq-grey">{{ resultCountLabel }}</span>
        <span v-if="lastUpdatedLabel && lastUpdatedAt" class="text-racq-grey">{{ lastUpdatedLabel }}</span>
      </slot>
    </div>
    <!-- Subheader for fuel finder price indicator -->
    <slot name="subHeader"></slot>

    <!-- Results List -->
    <div v-for="(item, index) of paginatedItems" :key="index" class="max-w-[920px]">
      <slot name="item" :item="item" :index="index" :isLast="index === paginatedItems.length - 1">
        <!-- Default item template -->
        <div class="py-4 border-b grid grid-cols-1 gap-2">
          <RichText
            :tag="item.url ? 'a' : 'div'"
            :href="item.url"
            class="text-base md:text-xl-bold link"
            :class="{ 'text-racq-dark-grey': !item.url }"
            :field="{ value: item.title }"
          />
          <RichText class="text-sm text-racq-med-grey" :field="{ value: item.description }" />
        </div>
      </slot>
    </div>

    <!-- Pagination -->
    <Pagination
      v-if="items?.length && showPagination"
      :total="items.length"
      :itemsPerPage="itemsPerPage"
      :defaultPage="currentPage"
      @updatePage="updatePage"
    />
  </div>
</template>

<script setup lang="ts">
import Pagination from '@/components/Pagination/Pagination.vue';
import { scrollToElement } from '@/utils/dom-helpers';
import { computed, ref, watch } from 'vue';
import { Field, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { replaceBookmark } from '@/utils/bookmark.replace';

interface Props {
  items: any[];
  content: {
    lastUpdatedLabel: Field<string>;
    resultCountLabel: Field<string>;
    paginationPrevLabel: Field<string>;
    paginationNextLabel: Field<string>;
  };
  itemsPerPage?: number;
  defaultPage?: number;
  lastUpdatedAt?: string;
  showPagination?: boolean;
  scrollTarget?: string;
}

const props = withDefaults(defineProps<Props>(), {
  itemsPerPage: 10,
  defaultPage: 1,
  showPagination: true,
  scrollTarget: '',
  lastUpdatedAt: '',
});

const emit = defineEmits<{
  pageChanged: [page: number];
}>();

const currentPage = ref(props.defaultPage);

const paginatedItems = computed(() => {
  return props.items.slice((currentPage.value - 1) * props.itemsPerPage, currentPage.value * props.itemsPerPage);
});

const lastUpdatedLabel = computed(() => {
  return replaceBookmark('lastUpdatedAt', props.content.lastUpdatedLabel?.value ?? '', props.lastUpdatedAt);
});

const resultCountLabel = computed(() => {
  return replaceBookmark('count', props.content.resultCountLabel?.value ?? '', props.items.length.toString());
});

const updatePage = (page: number) => {
  currentPage.value = page;
  emit('pageChanged', page);

  if (props.scrollTarget) {
    scrollToElement(props.scrollTarget);
  }
};

// Reset to first page when items change
watch(
  () => props.items,
  () => {
    currentPage.value = 1;
  },
  {
    deep: true,
  }
);
</script>
